import moment from 'moment';

// JSON:API entrega `id` como string e `learning-path-id` como número.
export const sameId = (a, b) => String(a) === String(b);

export const STATUS = {
  NOT_STARTED: 'not-started',
  IN_PROGRESS: 'in-progress',
  COMPLETED: 'completed'
};

export const STATUS_LABEL = {
  [STATUS.NOT_STARTED]: 'Não iniciada',
  [STATUS.IN_PROGRESS]: 'Em andamento',
  [STATUS.COMPLETED]: 'Concluída'
};

export const formatDate = value => (value ? moment(value).format('DD/MM/YY') : null);

// Mostra o percentual como a API devolve (23.53 → "23,53%"), sem arredondar pro inteiro.
export const formatPercent = value => `${(Number(value) || 0).toLocaleString('pt-BR', { maximumFractionDigits: 2 })}%`;

// Degradê da barra de progresso (percorrido) da trilha: card em destaque da lista e card da tela da trilha.
export const TRAIL_PROGRESS_GRADIENT = 'linear-gradient(90deg, #00B8D9 0%, #006C9C 100%)';

// `streak.level` da API (chave estável, calculada pelos dias seguidos) → rótulo e posição
// na escala (1–5, usada no cérebro/anel do painel de streak).
export const STREAK_LEVEL = {
  beginner: { rank: 1, label: 'Iniciante' },
  apprentice: { rank: 2, label: 'Aprendiz' },
  practitioner: { rank: 3, label: 'Praticante' },
  advanced: { rank: 4, label: 'Avançado' },
  master: { rank: 5, label: 'Mestre' }
};

// Bloco atual = primeiro ainda não concluído (ou o último, se acabou).
const blockProgressOf = (done, total) => {
  if (!total) return null;
  return { current: Math.min(done + 1, total), total };
};

// Une as trilhas do aluno (GET /trails, uma linha leve por trilha) e os templates
// fixos ainda não iniciados numa lista única de linhas.
export function buildTrilhaRows(templates, trails) {
  const fromUser = trails.map(trail => {
    const personalized = trail.kind === 'adaptive';
    const title = trail['display-name'];
    const status = trail.status;
    return {
      key: `trail-${trail.id}`,
      trailId: trail.id,
      chapterId: trail['latest-chapter-id'],
      chapter: trail.chapter,
      kind: trail.kind,
      personalized,
      title,
      // Trilha fixa sem objetivo vinculado mostra o próprio nome (ex.: "Objetivo: ENEM 2026").
      objective: trail['objective-name'] || (personalized ? null : title),
      subjectsCount: trail['subjects-count'],
      createdAt: trail['created-at'],
      lastAccessAt: trail['last-accessed-at'],
      percent: Number(trail['percent-walked']) || 0,
      // Não iniciada não mostra seção/missão nem nível (fica "-").
      blockProgress: status === STATUS.NOT_STARTED ? null : blockProgressOf(trail['blocks-done'], trail['blocks-total']),
      // Seção da trilha fixa ("Seção 2/4"); null na personalizada e na fixa gerada antes das seções.
      stage: trail['stage-total'] ? { current: trail['stage-current'], total: trail['stage-total'] } : null,
      deletable: !!trail.deletable,
      // `streak: null` = trilha ainda sem atividade: sem nível ("-").
      level: STREAK_LEVEL[trail.streak?.level] || null,
      status
    };
  });

  // Template fixo sem trilha do aluno ainda aparece na lista como "Não iniciada".
  // O adaptive não entra: ele é o "Criar trilha personalizada".
  const fromTemplates = templates
    .filter(t => t.kind !== 'adaptive' && !t['user-learning-path-id'])
    .map(t => ({
      key: `lp-${t.id}`,
      trailId: null,
      learningPathId: t.id,
      kind: t.kind,
      personalized: false,
      title: t.name,
      objective: t['objective-name'] || t.name,
      subjectsCount: null,
      createdAt: null,
      lastAccessAt: null,
      percent: 0,
      blockProgress: null,
      status: STATUS.NOT_STARTED,
      comingSoon: t.kind === 'enem'
    }));

  // A API só lista trilhas ativas. Uma personalizada com o capítulo concluído ("Concluída")
  // continua aparecendo: é por ela que o aluno gera o próximo capítulo.
  return [...fromUser, ...fromTemplates];
}

const timeOf = value => (value ? moment(value).valueOf() : 0);

// Destaque = trilha acessada por último (a API já ordena assim; no empate fica a primeira).
export function pickFeatured(rows) {
  const candidates = rows.filter(r => r.trailId);
  if (candidates.length === 0) return null;
  return candidates.reduce((best, row) => (timeOf(row.lastAccessAt) > timeOf(best.lastAccessAt) ? row : best));
}

// Coluna de progresso da lista: trilha fixa avança por seção, a personalizada
// por capítulo/missão. A missão atual é o bloco atual.
export function progressLabelOf(row) {
  if (!row.blockProgress) return null;
  const { current, total } = row.blockProgress;
  if (row.personalized) return `Capítulo ${row.chapter}, Missão ${current}`;
  return row.stage ? `Seção ${row.stage.current}/${row.stage.total}` : `Bloco ${current} de ${total}`;
}

// "Objetivo: ENEM • 10 matérias • Criada em 25/09/26"
export function metaLineOf(row, { objectiveSeparator = ': ' } = {}) {
  const parts = [];
  if (row.objective) parts.push(`Objetivo${objectiveSeparator}${row.objective}`);
  if (row.subjectsCount) parts.push(`${row.subjectsCount} ${row.subjectsCount === 1 ? 'matéria' : 'matérias'}`);
  if (row.personalized && row.createdAt) parts.push(`Criada em ${formatDate(row.createdAt)}`);
  return parts.join(' • ');
}
